import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Phone } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { getPost, POSTS } from "@/data/posts";
import { SITE } from "@/data/site";

export default function JournalPostPage() {
  const { slug } = useParams();
  const post = getPost(slug);

  if (!post) {
    return (
      <section className="flex min-h-[70vh] flex-col items-center justify-center bg-white px-6 pt-32 text-center">
        <p className="font-mono text-xs font-medium uppercase tracking-[0.18em] text-gold-deep">Not found</p>
        <h1 className="mt-4 text-3xl font-bold text-navy-900">That article doesn't exist.</h1>
        <Link to="/journal" className="mt-8 inline-flex items-center gap-2 rounded-md bg-navy-900 px-6 py-3 text-sm font-semibold text-white" data-testid="journal-notfound-back-link">
          <ArrowLeft className="h-4 w-4" /> The Journal
        </Link>
      </section>
    );
  }

  const others = POSTS.filter((p) => p.slug !== slug).slice(0, 2);

  return (
    <>
      <Seo
        title={`${post.title} — SAIVU Journal`}
        description={post.excerpt}
        path={`/journal/${post.slug}`}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Article",
          headline: post.title,
          description: post.excerpt,
          author: { "@type": "Organization", name: "SAIVU — Saivu Consulting Pvt Ltd" },
          publisher: { "@type": "Organization", name: "SAIVU" },
          mainEntityOfPage: `https://saivu.in/journal/${post.slug}`,
        }}
      />
      <article className="bg-white pt-28 pb-16 sm:pt-32" data-testid="journal-post">
        <div className="mx-auto max-w-3xl px-5 sm:px-6">
          <Reveal>
            <Link to="/journal" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.14em] text-slate-600 hover:text-navy-900" data-testid="journal-back-link">
              <ArrowLeft className="h-3.5 w-3.5" /> Journal
            </Link>
            <p className="mt-8 font-mono text-[11px] font-medium uppercase tracking-[0.14em] text-gold-deep">
              {post.category} · {post.date} · {post.readTime}
            </p>
            <h1 className="mt-4 text-3xl font-bold leading-[1.2] tracking-tight text-navy-900 sm:text-4xl">
              {post.title}
            </h1>
          </Reveal>
          <div className="mt-10 flex flex-col gap-9">
            {post.body.map((section, i) => (
              <Reveal key={section.h} delay={i * 0.03}>
                <h2 className="text-xl font-semibold text-navy-900 sm:text-2xl">{section.h}</h2>
                {section.p.map((para) => (
                  <p key={para.slice(0, 24)} className="mt-4 text-base leading-relaxed text-ink">
                    {para}
                  </p>
                ))}
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-14">
            <div className="rounded-lg bg-navy-900 p-8 sm:p-10" data-testid="journal-post-cta">
              <h2 className="text-2xl font-semibold text-white">Rather hand this to a professional?</h2>
              <p className="mt-3 text-base leading-relaxed text-navy-100">
                Book a free 20-minute consultation — we'll map out the exact next step for
                your business.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link to="/contact" className="rounded-md bg-white px-6 py-3 text-sm font-semibold text-navy-900 transition-colors hover:bg-navy-50" data-testid="journal-cta-consult">
                  Book free consult
                </Link>
                <a href={`tel:${SITE.phoneTel}`} className="inline-flex items-center gap-2 rounded-md border border-white/40 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-white/10" data-testid="journal-cta-call">
                  <Phone className="h-4 w-4" /> {SITE.phoneDisplay}
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </article>

      <section className="border-t border-line bg-mist py-14" data-testid="journal-more">
        <div className="mx-auto max-w-3xl px-5 sm:px-6">
          <h2 className="text-xl font-semibold text-navy-900">Keep reading</h2>
          <div className="mt-6 flex flex-col gap-4">
            {others.map((p) => (
              <Link key={p.slug} to={`/journal/${p.slug}`} data-testid={`journal-more-${p.slug}`} className="group rounded-lg border border-line bg-white p-6 transition-all hover:border-navy-100 hover:shadow-card">
                <span className="font-mono text-[11px] font-medium uppercase tracking-[0.14em] text-gold-deep">{p.category}</span>
                <span className="mt-2 block text-lg font-semibold text-navy-900">{p.title}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
